import { PropsWithChildren, StrictMode, Suspense, useEffect } from 'react';
import { PacklinkProvider } from '@packlink/provider';
import { TenantProvider, useTenant } from '@packlink/tenant-provider';
import { TenantConfigProvider } from '@packlink/tenant-config-provider';
import { Application } from '@packlink/packlink-sdk';
import { SdkProvider } from '@packlink/packlink-sdk-provider';

import PacklinkSDK from '@sdk';
import { useLogout } from '@hooks';
import { BrandProvider } from './brand';
import { TranslationProvider } from './translation';
import { AxiosError, AxiosRequestConfig } from 'axios';

interface InterceptorConfig extends AxiosRequestConfig {
    _retry?: boolean;
}
interface InterceptorError extends AxiosError {
    config: InterceptorConfig;
}

// Redirect to login if token and refresh token are expired
function useLogoutOnExpiredSession() {
    const doLogout = useLogout();

    useEffect(() => {
        PacklinkSDK.apiClient.client.interceptors.response.use(
            (response) => {
                return response;
            },
            async (error: InterceptorError) => {
                const originalRequest = error.config;
                const auth = PacklinkSDK.apiClient.getAuthorization();
                const refreshExpired =
                    auth && error.response && error.response.status === 401 && originalRequest._retry;
                const sessionV1Expired = auth && error.response && error.response.status === 401;

                if (refreshExpired || sessionV1Expired) {
                    doLogout();
                }
                return Promise.reject(error);
            },
        );
    }, [doLogout]);
}

function ConfigureSDK({ children }: PropsWithChildren<Record<string, unknown>>): JSX.Element {
    const tenant = useTenant();
    useLogoutOnExpiredSession();

    useEffect(() => {
        PacklinkSDK.setTenant(tenant);
    }, [tenant]);

    return <>{children}</>;
}

export function SFPacklinkProviders({ children }: PropsWithChildren<Record<string, unknown>>): JSX.Element {
    return (
        <PacklinkProvider cdnUrl={config.cdn.url} application={Application.SINGLE_FLOW}>
            <TenantProvider application={Application.SINGLE_FLOW}>
                <TenantConfigProvider application={Application.SINGLE_FLOW} cdnUrl={config.cdn.url}>
                    <SdkProvider sdk={PacklinkSDK}>
                        <Suspense fallback={<p>loading...</p>}>
                            <TranslationProvider>
                                <BrandProvider>
                                    <ConfigureSDK>
                                        <StrictMode>{children}</StrictMode>
                                    </ConfigureSDK>
                                </BrandProvider>
                            </TranslationProvider>
                        </Suspense>
                    </SdkProvider>
                </TenantConfigProvider>
            </TenantProvider>
        </PacklinkProvider>
    );
}
